import React, { useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { SliceGroup, SliceInvestor, SliceSidebarTab } from "store/types";

import { usePortfolioConstructionContext } from "../Context/PortfolioConstructionContext";

interface Props {
  title?: string;
}

export const SliceSidebar: React.FC<Props> = ({ title = "Slicer" }) => {
  const {
    groups,
    investors,
    sliceTab: tab,
    setSliceTab,
    selectedGroupId,
    setSelectedGroupId,
    selectedInvestorId,
    setSelectedInvestorId,
    onAddGroup,
    onAddInvestor,
    onEditSlice,
    onDuplicateSlice,
    onDeleteSlice,
    permissions
  } = usePortfolioConstructionContext();

  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  const filteredInvestors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return investors;
    return investors.filter((i) => i.name.toLowerCase().includes(q));
  }, [investors, search]);

  const handleTabClick = (next: SliceSidebarTab) => {
    setSliceTab(next);
    setSearch("");
  };

  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<SliceGroup["id"]>>(new Set());

  const handleSelectGroup = (group: SliceGroup) => {
    setSelectedGroupId(group.id);
  };

  const handleToggleExpand = (group: SliceGroup) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(group.id)) next.delete(group.id);
      else next.add(group.id);
      return next;
    });
  };

  const handleSelectInvestor = (investor: SliceInvestor) => {
    setSelectedInvestorId(investor.id);
  };

  const isInvestorsTab = tab === "investors";

  return (
    <Stack
      sx={{
        width: "100%",
        height: "325px",
        flexShrink: 0,
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "16px",
        gap: "10px",
        boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)",
        overflow: "hidden"
      }}
    >
      <Typography
        sx={{
          flexShrink: 0,

          fontSize: "18px",
          fontWeight: 500,
          lineHeight: "24px",
          color: "#071f35",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {title}
      </Typography>

      <Stack direction="row" sx={{ flexShrink: 0, gap: "16px", alignItems: "stretch" }}>
        <SliceTab
          icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
          label="Investors"
          active={isInvestorsTab}
          onClick={() => handleTabClick("investors")}
        />
        <Box sx={{ width: "1px", backgroundColor: "#dbe8f5", mb: "6px" }} />
        <SliceTab
          icon={<PeopleOutlineIcon sx={{ fontSize: 16 }} />}
          label="Slice"
          active={!isInvestorsTab}
          onClick={() => handleTabClick("slice")}
        />
      </Stack>

      <Stack direction="row" alignItems="center" gap="10px" sx={{ flexShrink: 0, width: "100%" }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isInvestorsTab ? "Search an investor" : "Search a group"}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: "4px" }}>
                <SearchIcon sx={{ fontSize: 16, color: "#071f35" }} />
              </InputAdornment>
            ),
            sx: {
              fontSize: "14px",
              lineHeight: "16px",
              color: "#071f35",
              borderRadius: "8px",
              "& input": { padding: "8px 0" },
              "& input::placeholder": { color: "#92a7bc", opacity: 1 },
              "& fieldset": { borderColor: "#7b92a9" }
            }
          }}
        />
        {(isInvestorsTab || permissions.canCreateSlice) && (
          <Button
            onClick={isInvestorsTab ? onAddInvestor : onAddGroup}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              color: "#26a6ae",

              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "16px",
              textTransform: "none",
              padding: "4px 8px",
              borderRadius: "32px",
              minWidth: 0,
              flexShrink: 0,
              "& .MuiButton-startIcon": { marginLeft: 0, marginRight: "4px" },
              "&:hover": { backgroundColor: "rgba(38, 166, 174, 0.08)" }
            }}
          >
            Add
          </Button>
        )}
      </Stack>

      <Stack
        sx={{
          flex: "1 1 0",
          minHeight: 0,
          gap: "4px",
          width: "100%",
          overflowY: "auto",
          scrollbarGutter: "stable"
        }}
      >
        {isInvestorsTab
          ? filteredInvestors.map((investor) => {
              const isSelected = investor.id === selectedInvestorId;
              return (
                <Box
                  key={investor.id}
                  onClick={() => handleSelectInvestor(investor)}
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                    backgroundColor: isSelected ? "#dbe8f5" : "transparent",
                    padding: "4px 8px",
                    cursor: "pointer",
                    "&:hover": isSelected ? undefined : { backgroundColor: "rgba(219, 232, 245, 0.4)" }
                  }}
                >
                  <MaterialTooltip title={investor.name}>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: isSelected ? 700 : 400,
                        lineHeight: "24px",
                        color: "#071f35",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {investor.name}
                    </Typography>
                  </MaterialTooltip>
                </Box>
              );
            })
          : filteredGroups.map((group) => {
              const isSelected = group.id === selectedGroupId;
              const isExpanded = expandedGroupIds.has(group.id);
              return (
                <Box
                  key={group.id}
                  sx={{
                    width: "100%",
                    borderRadius: "4px",
                    backgroundColor: isSelected ? "#dbe8f5" : "transparent",
                    padding: "4px 8px",
                    cursor: "pointer",
                    "&:hover": isSelected ? undefined : { backgroundColor: "rgba(219, 232, 245, 0.4)" }
                  }}
                  onClick={() => handleSelectGroup(group)}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap="8px">
                    <MaterialTooltip title={group.name}>
                      <Typography
                        sx={{
                          flex: "1 1 0",
                          minWidth: 0,

                          fontSize: "16px",
                          fontWeight: isSelected ? 700 : 400,
                          lineHeight: "24px",
                          color: "#071f35",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {group.name}
                      </Typography>
                    </MaterialTooltip>
                    <SliceActionsMenu
                      canEdit={permissions.canEditSlice(group)}
                      canDuplicate={permissions.canDuplicateSlice}
                      canArchive={permissions.canArchiveSlice(group)}
                      onEdit={() => onEditSlice(group)}
                      onDuplicate={() => onDuplicateSlice(group)}
                      onArchive={() => onDeleteSlice(group)}
                    />
                    <IconButton
                      disableRipple
                      sx={{
                        width: 24,
                        height: 24,
                        padding: 0,
                        color: "#071f35",
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: "rgba(7,31,53,0.08)" }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleExpand(group);
                      }}
                    >
                      {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Stack>

                  {isExpanded && group.members.length > 0 && (
                    <Stack sx={{ gap: "8px", paddingY: "4px" }}>
                      {group.members.map((member) => (
                        <MaterialTooltip key={member.id} title={member.name}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "16px",
                              color: "#071f35",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}
                          >
                            {member.name}
                          </Typography>
                        </MaterialTooltip>
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
      </Stack>
    </Stack>
  );
};

interface SliceTabProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

interface SliceActionsMenuProps {
  canEdit: boolean;
  canDuplicate: boolean;
  canArchive: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
}

const SliceActionsMenu: React.FC<SliceActionsMenuProps> = ({
  canEdit,
  canDuplicate,
  canArchive,
  onEdit,
  onDuplicate,
  onArchive
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  if (!canEdit && !canDuplicate && !canArchive) return null;

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const run = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    action();
  };

  return (
    <>
      <IconButton
        disableRipple
        onClick={handleOpen}
        sx={{
          width: 24,
          height: 24,
          padding: 0,
          color: "#071f35",
          borderRadius: "50%",
          "&:hover": { backgroundColor: "rgba(7,31,53,0.08)" }
        }}
      >
        <MoreVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { borderRadius: "8px", minWidth: 160 } } }}
      >
        {canEdit && (
          <MenuItem onClick={run(onEdit)}>
            <ListItemIcon>
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Edit</ListItemText>
          </MenuItem>
        )}
        {canDuplicate && (
          <MenuItem onClick={run(onDuplicate)}>
            <ListItemIcon>
              <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Duplicate</ListItemText>
          </MenuItem>
        )}
        {canArchive && (
          <MenuItem onClick={run(onArchive)} sx={{ color: "#d93e3e" }}>
            <ListItemIcon>
              <ArchiveOutlinedIcon sx={{ fontSize: 18, color: "#d93e3e" }} />
            </ListItemIcon>
            <ListItemText slotProps={{ primary: { fontSize: 14 } }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const SliceTab: React.FC<SliceTabProps> = ({ icon, label, active, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    onClick={onClick}
    sx={{
      gap: "8px",
      padding: "2px 0 6px 0",
      cursor: "pointer",
      borderBottom: active ? `2px solid ${"#071f35"}` : "2px solid transparent",
      color: active ? "#071f35" : "#496682"
    }}
  >
    {icon}
    <Typography
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "16px",
        color: "inherit",
        whiteSpace: "nowrap"
      }}
    >
      {label}
    </Typography>
  </Stack>
);
